"use client"

import React, { Suspense, useMemo, useCallback, useState, useRef, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Link } from '@/i18n/navigation'
import { getCachedVideoUrl } from "@/utils/videoUrlCache"
import {
  videoPreloadManager
} from "@/utils/videoPerformance"

interface Feature {
  title: string
  description: string
}

interface AdvancedFeature {
  title: string
  bulletPoints: string[]
}

interface FeatureShowcaseProps {
  title: string
  description: string
  features: Feature[]
  advancedFeature: AdvancedFeature
  video: {
    url: string
    poster?: string
    aspectRatio: '16:9' | '9:16' | '1:1'
  }
  buttonText: string
  buttonHref: string
  contentPosition?: "left" | "right"
}

interface VideoState {
  isLoaded: boolean;
  isMuted: boolean;
  loadError: boolean;
  isVisible: boolean;
  showVideo: boolean;
  video?: {
    url: string;
    poster?: string;
    aspectRatio: '16:9' | '9:16' | '1:1';
  };
}

// SSR文字内容组件
const Feature4TextContent: React.FC<{
  title: string;
  description: string;
  features: Feature[];
  advancedFeature: AdvancedFeature;
  buttonText: string;
  buttonHref: string;
}> = ({ title, description, features, advancedFeature, buttonText, buttonHref }) => {
  return (
    <div className="flex-1 space-y-6 sm:space-y-8 lg:space-y-10">
      {/* 主标题和描述 */}
      <div className="space-y-4 sm:space-y-6">
        <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold bg-gradient-to-r from-white via-green-100 to-green-300 bg-clip-text text-transparent leading-tight">
          {title}
        </h2>
        <p className="text-sm sm:text-base lg:text-lg text-gray-300 font-medium leading-relaxed max-w-2xl">
          {description}
        </p>
      </div>

      {/* 功能列表 */}
      <div className="space-y-4 sm:space-y-6">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-white">
          Key Features
        </h3>
        <div className="grid gap-3 sm:gap-4">
          {features.map((feature, index) => (
            <div key={index} className="flex items-start gap-3 sm:gap-4">
              <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-gradient-to-br from-green-500 to-emerald-600 rounded-lg flex items-center justify-center">
                <svg className="w-3 h-3 sm:w-4 sm:h-4 text-black" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <h4 className="text-sm sm:text-base lg:text-lg font-semibold text-white mb-1">
                  {feature.title}
                </h4>
                <p className="text-xs sm:text-sm lg:text-base text-gray-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 高级功能 */}
      <div className="space-y-4 sm:space-y-6">
        <h3 className="text-lg sm:text-xl lg:text-2xl font-semibold text-white">
          {advancedFeature.title}
        </h3>
        <div className="grid gap-2 sm:gap-3">
          {advancedFeature.bulletPoints.map((point, index) => (
            <div key={index} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-2 h-2 sm:w-3 sm:h-3 bg-green-400 rounded-full mt-2 sm:mt-3" />
              <p className="text-xs sm:text-sm lg:text-base text-gray-300 leading-relaxed">
                {point}
              </p>
            </div>
          ))}
        </div>
      </div>

      {/* CTA按钮 */}
      <div className="pt-4 sm:pt-6">
        <Button
          className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-3 sm:px-4 lg:px-6 py-2 sm:py-2.5 lg:py-3 text-xs sm:text-sm lg:text-base rounded-lg sm:rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 w-full sm:w-auto max-w-xs sm:max-w-none"
          asChild
          aria-label={buttonText}
        >
          <Link href={buttonHref}>
            <span className="flex items-center justify-center gap-1.5 sm:gap-2">
              <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              <span className="whitespace-nowrap">{buttonText}</span>
            </span>
          </Link>
        </Button>
      </div>
    </div>
  );
};

// 客户端视频组件
const Feature4VideoContent: React.FC<{
  video: {
    url: string
    poster?: string
    aspectRatio: '16:9' | '9:16' | '1:1'
  }
  contentPosition?: "left" | "right"
}> = ({ video, contentPosition = "left" }) => {
  const [videoState, setVideoState] = useState<VideoState>({
    isLoaded: false,
    isMuted: true,
    loadError: false,
    isVisible: false,
    showVideo: false,
    video
  });
  const [isClient, setIsClient] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // 客户端检测
  useEffect(() => {
    setIsClient(true);
  }, []);

  // 简化的视频加载逻辑
  useEffect(() => {
    if (!isClient || !videoRef.current) return;

    const videoElement = videoRef.current;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVideoState(prev => ({ ...prev, isVisible: true, showVideo: true }));
        }
      },
      { threshold: 0.1 }
    );

    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    return () => observer.disconnect();
  }, [isClient]);

  useEffect(() => {
    if (!isClient || !videoRef.current) return;

    const videoElement = videoRef.current;
    const videoUrl = video.url;

    const handleLoadedData = () => {
      setVideoState(prev => ({ ...prev, isLoaded: true }));
    };

    const handleError = (error) => {
      setVideoState(prev => ({ ...prev, loadError: true }));
      console.error('Video load error:', error);
    };

    const handleCanPlay = () => {
      // 视频可以播放
    };

    videoElement.addEventListener('loadeddata', handleLoadedData);
    videoElement.addEventListener('error', handleError);
    videoElement.addEventListener('canplay', handleCanPlay);

    return () => {
      videoElement.removeEventListener('loadeddata', handleLoadedData);
      videoElement.removeEventListener('error', handleError);
      videoElement.removeEventListener('canplay', handleCanPlay);
    };
  }, [isClient, video.url]);

  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    
    const videoElement = videoRef.current;
    videoElement.muted = !videoElement.muted;
    setVideoState(prev => ({ ...prev, isMuted: videoElement.muted }));
  }, []);

  return (
    <div 
      ref={containerRef}
      className={`flex-1 relative ${contentPosition === "right" ? "order-first lg:order-last" : ""}`}
      style={{ boxSizing: 'border-box' }}
    >
      <div 
        className="relative aspect-video bg-black rounded-xl sm:rounded-2xl overflow-hidden border-2 border-green-500/30"
        style={{ 
          boxSizing: 'border-box',
          minHeight: 0,
          maxHeight: 'none'
        }}
      >
        {/* 加载状态 - 使用绝对定位覆盖在视频上 */}
        {!isClient && (
          <div 
            className="absolute inset-0 w-full h-full bg-gradient-to-br from-gray-900 to-black flex items-center justify-center z-10"
            style={{ boxSizing: 'border-box' }}
          >
            <div className="text-center">
              <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 mx-auto mb-2 sm:mb-3 lg:mb-4 border-4 border-emerald-400/30 border-t-emerald-400 rounded-full animate-spin"></div>
              <p className="text-sm sm:text-base lg:text-lg font-semibold mb-1 sm:mb-2 text-white">AI Video Loading</p>
              <p className="text-xs sm:text-sm text-gray-300">Loading...</p>
              <div className="w-2 h-2 bg-emerald-400 rounded-full mx-auto mt-3 animate-pulse"></div>
            </div>
          </div>
        )}
        
        {/* 视频元素 - 始终存在，但可能被加载状态覆盖 */}
        <video
          ref={videoRef}
          className="w-full h-full object-cover"
          style={{ 
            boxSizing: 'border-box',
            display: 'block',
            verticalAlign: 'top'
          }}
          poster={video.poster}
          muted={videoState.isMuted}
          loop
          playsInline
          autoPlay
          preload="metadata"
          onError={(e) => {
            console.error('Video error:', e);
          }}
        >
          <source src={video.url} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        
        {/* 音频控制按钮 */}
        <div className="absolute bottom-4 right-4 z-20">
          <button
            onClick={toggleMute}
            className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 bg-black/60 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-emerald-500/20 transition-all duration-300 border border-emerald-400/30 pointer-events-auto"
            aria-label={videoState.isMuted ? "Unmute" : "Mute"}
          >
            {videoState.isMuted ? (
              <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2" />
              </svg>
            ) : (
              <svg className="w-3 h-3 sm:w-4 sm:h-4 lg:w-5 lg:h-5 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.536 8.464a5 5 0 010 7.072m2.828-9.9a9 9 0 010 14.142M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z" />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

// 主组件 - 组合SSR文字和客户端视频
const OptimizedFeature4: React.FC<FeatureShowcaseProps> = ({ 
  title,
  description,
  features,
  advancedFeature,
  video,
  buttonText,
  buttonHref,
  contentPosition = "left"
}) => {
  return (
    <section className="relative bg-gradient-to-b from-black via-gray-950 to-black overflow-hidden py-12 sm:py-16 lg:py-20">
      {/* 背景装饰 */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_40%,rgba(34,197,94,0.08),transparent_50%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_60%,rgba(16,185,129,0.06),transparent_50%)]" />
      
      <div className="container relative z-10 px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
          {/* SSR文字内容 */}
          <Feature4TextContent
            title={title}
            description={description}
            features={features}
            advancedFeature={advancedFeature}
            buttonText={buttonText}
            buttonHref={buttonHref}
          />
          
          {/* 客户端视频内容 */}
          <Suspense fallback={
            <div className="flex-1 aspect-video bg-black rounded-xl sm:rounded-2xl animate-pulse" />
          }>
            <Feature4VideoContent
              video={video}
              contentPosition={contentPosition}
            />
          </Suspense>
        </div>
      </div>
    </section>
  );
};

export default OptimizedFeature4; 